// src/pages/Recruit.jsx
import React from "react";
import { Box, Container, Typography, Stack, Button, Grid, Breadcrumbs, Link } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import HeroWave from "../../../GlobalFunction/HeroWave";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

/* ----------------------------- Angled Tile ------------------------------ */
function AngledTile({ src, alt, offset = 0 }) {
  return (
    <Box
      sx={{
        // individual tile wrapper to shift up/down
        transform: {
          xs: `translateY(${offset / 2}px)`,
          md: `translateY(${offset}px)`,
        },
        willChange: "transform",
      }}
    >
      <Box
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 10px 28px rgba(0,0,0,.14)",
          transition:
            "transform 240ms cubic-bezier(0.34,1.56,0.64,1), box-shadow 240ms ease, filter 180ms ease",
          width: { xs: 200, sm: 260, md: 300 }, // fixed card width for smooth loop
          aspectRatio: "16 / 9",
            clipPath: "polygon(8% 0, 100% 0, 92% 100%, 0 100%)",
          backgroundColor: "#fff",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: "0 18px 46px rgba(0,0,0,.18)",
            filter: "saturate(1.05)",
          },
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
}

/* ------------------------ Endless Scrolling Strip ------------------------ */
// CSS keyframes (MUI SX)
const stripSx = {
  position: "relative",
  overflow: "hidden",
  // prevent scrollbar wobble
  maskImage:
    "linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,1) 6%, rgba(0,0,0,1) 94%, rgba(0,0,0,0))",
  "&:hover .scroll-track": {
    animationPlayState: "paused",
  },
};

const trackSx = {
  display: "flex",
  alignItems: "center",
  //   gap: { xs: 12, md: 20 }, // space between tiles
  gap: 0, // we’ll control spacing per-tile for a tighter look
  width: "max-content",
  // endless scroll
  animation: "recruit-marquee 28s linear infinite",
  "& > *": { flex: "0 0 auto" },
  "@keyframes recruit-marquee": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(-50%)" }, // move by half because we duplicate content
  },
};

export default function Recruit({ mode }) {
  const accent = getPrimaryColor(mode);

  // Alternating offsets to mimic “up/down” placement in the mock
  const tiles = [
    { src: "/img/companyInfo/recruit1.jpg", alt: "recruit 1", offset: -14 },
    { src: "/img/companyInfo/recruit2.jpg", alt: "recruit 2", offset: 14 },
    { src: "/img/companyInfo/recruit3.jpg", alt: "recruit 3", offset: -14 },
    { src: "/img/companyInfo/recruit4.jpg", alt: "recruit 4", offset: 14 },
    { src: "/img/companyInfo/recruit5.jpg", alt: "recruit 5", offset: -14 },
  ];

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      {/* top hero with same wave */}
      <HeroWave image={"/img/companyInfo/recruitTop.png"} h3={"合作資訊"} />

       <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href="/">首頁</Link>
          <Link underline="hover" color="inherit" href="/合作資訊">合作資訊</Link>
          <Typography color="text.primary">招募訊息</Typography>
        </Breadcrumbs>
      </Container>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          maxWidth: 1180,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 5, md: 7 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
          招募訊息
        </Typography>

        {/* Endless staggered strip */}
        <Box sx={stripSx}>
          {/* We render the track twice consecutively for a seamless loop */}
          <Box className="scroll-track" sx={trackSx}>
            {tiles.map((t, i) => (
              <AngledTile key={`a-${i}`} {...t} />
            ))}
            {tiles.map((t, i) => (
              <AngledTile key={`b-${i}`} {...t} />
            ))}
          </Box>
        </Box>

        {/* CTA buttons */}
        <Stack
          direction="row"
          spacing={{ xs: 2.5, md: 4 }}
          justifyContent="center"
          sx={{ mt: { xs: 3, md: 4 }, mb: { xs: 6, md: 8 } }}
        >
          <Button
            component={RouterLink}
            to="/recruit/new-grads"
            variant="contained"
            sx={{
              px: { xs: 2.5, md: 4 },
              py: 1,
              fontWeight: 800,
              letterSpacing: 1,
              borderRadius: 1.5,
              bgcolor: accent,
              boxShadow: "0 6px 14px rgba(0,0,0,.12)",
              "&:hover": {
                bgcolor: alpha(accent, 0.9),
                transform: "translateY(-2px)",
              },
              transition: "transform .18s ease",
            }}
          >
            應屆畢業生招聘
          </Button>
          <Button
            component={RouterLink}
            to="/recruit/professionals"
            variant="contained"
            sx={{
              px: { xs: 2.5, md: 4 },
              py: 1,
              fontWeight: 800,
              letterSpacing: 1,
              borderRadius: 1.5,
              bgcolor: accent,
              boxShadow: "0 6px 14px rgba(0,0,0,.12)",
              "&:hover": {
                bgcolor: alpha(accent, 0.9),
                transform: "translateY(-2px)",
              },
              transition: "transform .18s ease",
            }}
          >
            專業人士招聘
          </Button>
        </Stack>

        {/* Other sections (unchanged) */}
        <Grid container spacing={{ xs: 6, md: 8 }}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
              應徵流程
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
              工作環境
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
              認識公司
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
